<template>
  <div class="resource-preview">
    <div v-if="resource.type === 'video'" class="video-container">
      <iframe
        v-if="isYouTubeVideo"
        :src="youtubeEmbedUrl"
        class="responsive-iframe"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      />
      <video v-else :src="resource.url" controls class="video-player">
        {{ $t('VIDEO_NOT_SUPPORTED') }}
      </video>
    </div>

    <div v-else-if="resource.type === 'audio'" class="audio-container">
      <audio
        :src="resource.url"
        controls
        class="audio-player"
        controlsList="nodownload"
      >
        {{ $t('AUDIO_NOT_SUPPORTED') }}
      </audio>
      <div class="audio-info">
        <i class="el-icon-headset"></i>
        <span class="audio-filename">{{
          getFilenameFromUrl(resource.url)
        }}</span>
      </div>
    </div>

    <div v-else-if="resource.type === 'image'" class="image-container">
      <img :src="resource.url" :alt="resource.url" class="image-preview" />
    </div>

    <div v-else-if="resource.type === 'document'" class="document-container">
      <a :href="resource.url" target="_blank" class="document-link">
        <i class="el-icon-document"></i>
        <span>{{ $t('VIEW_DOCUMENT') }}</span>
      </a>
    </div>

    <div v-else class="unsupported-type">
      {{ $t('UNSUPPORTED_RESOURCE_TYPE') }}: {{ resource.type }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResourcePreview',
  props: {
    resource: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          value.type &&
          ['image', 'audio', 'video', 'document'].includes(value.type) &&
          value.url
        )
      }
    }
  },

  computed: {
    isYouTubeVideo() {
      if (this.resource.type !== 'video') return false
      const url = this.resource.url
      const youtubePatterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)/i
      ]
      return youtubePatterns.some((pattern) => pattern.test(url))
    },

    youtubeEmbedUrl() {
      if (!this.isYouTubeVideo) return ''

      const url = this.resource.url
      let videoId = ''

      // Extract video ID from different YouTube URL formats
      if (url.includes('youtube.com/watch?v=')) {
        const urlParams = new URL(url).searchParams
        videoId = urlParams.get('v')
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0]
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('youtube.com/embed/')[1].split('?')[0]
      }

      return videoId ? `https://www.youtube.com/embed/${videoId}` : url
    }
  },

  methods: {
    getFilenameFromUrl(url) {
      if (!url) return 'Audio file'
      try {
        const urlObj = new URL(url)
        const pathname = urlObj.pathname
        const filename = pathname.substring(pathname.lastIndexOf('/') + 1)
        return filename || 'Audio file'
      } catch (e) {
        return 'Audio file'
      }
    }
  }
}
</script>

<style scoped lang="scss">
.resource-preview {
  width: 100%;
  margin: 10px 0;

  .video-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; // 16:9 aspect ratio
    height: 0;
    overflow: hidden;
    background-color: #000;
    border-radius: 8px;

    .responsive-iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 8px;
    }

    .video-player {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 8px;
    }
  }

  .audio-container {
    width: 100%;
    padding: 20px;
    background-color: #f5f5f5;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .audio-player {
      width: 100%;
      outline: none;
      min-height: 40px;
    }

    .audio-info {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #606266;
      font-size: 14px;

      i {
        font-size: 18px;
        color: #409eff;
      }

      .audio-filename {
        word-break: break-all;
      }
    }
  }

  .image-container {
    width: 100%;
    text-align: center;
    border-radius: 8px;
    overflow: hidden;

    .image-preview {
      max-width: 100%;
      height: auto;
      display: inline-block;
      border-radius: 8px;
    }
  }

  .document-container {
    padding: 20px;
    background-color: #f5f5f5;
    border-radius: 8px;
    text-align: center;

    .document-link {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #409eff;
      font-size: 16px;
      text-decoration: none;
      transition: color 0.3s;

      &:hover {
        color: #66b1ff;
        text-decoration: underline;
      }

      i {
        font-size: 24px;
      }
    }
  }

  .unsupported-type {
    padding: 20px;
    text-align: center;
    color: #909399;
    background-color: #f5f5f5;
    border-radius: 8px;
  }
}
</style>
